import { Upload } from "tus-js-client";
import { uploadErrorMessage } from "./upload-error";

export interface ResumableUploadParams {
  endpoint: string;
  bucket: string;
  objectPath: string;
  blob: Blob;
  accessToken: string;
  anonKey: string;
  contentType: string;
  onProgress?: (pct: number) => void;
}

/** `detail` : motif technique, jamais affiché (classement de l'échec, signalement). */
export type ResumableUploadResult = { ok: true } | { ok: false; error: string; detail?: string };
export type ResumableUploadFn = (p: ResumableUploadParams) => Promise<ResumableUploadResult>;

// chunkSize DOIT valoir 6 Mo : exigence actuelle du serveur TUS Supabase
// (doc resumable-uploads, « do not change it »).
const CHUNK_SIZE = 6 * 1024 * 1024;

// Implémentation par défaut via tus-js-client. Couche d'intégration fine
// (réseau, non testée unitairement) : reprise auto sur coupure réseau et
// fichiers volumineux, contrairement au standard upload supabase-js.
export const defaultResumableUpload: ResumableUploadFn = (p) =>
  new Promise<ResumableUploadResult>((resolve) => {
    const upload = new Upload(p.blob, {
      endpoint: p.endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        authorization: `Bearer ${p.accessToken}`,
        apikey: p.anonKey,
        // upsert=true : retry idempotent. Si l'audio a déjà été uploadé (1er essai)
        // mais qu'une étape ultérieure a échoué (ex. insert DB), « Réessayer » ré-uploade
        // le MÊME fichier (objectPath = userId/captureId, unique) sans 409 « already exists ».
        "x-upsert": "true",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      chunkSize: CHUNK_SIZE,
      metadata: {
        bucketName: p.bucket,
        objectName: p.objectPath,
        contentType: p.contentType,
        cacheControl: "3600",
      },
      // DS §6.13 : le message du serveur TUS ne sort pas d'ici (« The object
      // exceeded the maximum allowed size », motifs réseau de la WKWebView).
      onError: (error) =>
        resolve({
          ok: false,
          error: uploadErrorMessage("captures.upload.resumable", error.message),
          detail: error.message,
        }),
      onProgress: (uploaded, total) => {
        if (p.onProgress && total > 0) {
          p.onProgress(Math.round((uploaded / total) * 100));
        }
      },
      onSuccess: () => resolve({ ok: true }),
    });

    upload
      .findPreviousUploads()
      .then((previous) => {
        const [first] = previous;
        if (first) upload.resumeFromPreviousUpload(first);
        upload.start();
      })
      .catch(() => upload.start());
  });
